import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Trash2, Loader2, FileSpreadsheet, Database, Wifi,
  RefreshCw, Calendar, ArrowDownToLine, Activity, CheckCircle2, AlertCircle
} from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";

import SspScheduleEditor from "./SspScheduleEditor";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MarketplaceUploads = ({ token, requestDelete, onUploadComplete }) => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // API Fetch state
  const [fetchingSSP, setFetchingSSP] = useState(null);
  const [fetchDateFrom, setFetchDateFrom] = useState("");
  const [fetchDateTo, setFetchDateTo] = useState("");
  const [showFetchDialog, setShowFetchDialog] = useState(false);
  const [activeFetchSSP, setActiveFetchSSP] = useState(null);
  const [fetchResult, setFetchResult] = useState(null);

  // Test Connection state
  const [testingSSP, setTestingSSP] = useState(null);

  // Scheduler state
  const [scheduler, setScheduler] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [triggeringScheduler, setTriggeringScheduler] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUploads = useCallback(async () => {
    setLoading(true);
    try {
      const [uploadsRes, connsRes, schedRes] = await Promise.all([
        axios.get(`${API}/reporting/marketplace/uploads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reporting/marketplace/api-connections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reporting/marketplace/scheduler`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      setUploads(uploadsRes.data || []);
      setConnections(connsRes.data || []);
      if (schedRes?.data) {
        setScheduler(schedRes.data);
        setSchedules(schedRes.data.schedules || []);
      }
    } catch {
      toast.error("Failed to load uploads");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Select a file"); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const res = await axios.post(`${API}/reporting/marketplace/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      toast.success(`Uploaded ${res.data.row_count} rows from ${res.data.ssp_source}`);
      fetchUploads();
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (upload) => {
    requestDelete("marketplace_upload", upload.upload_id, `${upload.ssp_source} - ${upload.filename}`, async () => {
      try {
        await axios.delete(`${API}/reporting/marketplace/uploads/${upload.upload_id}`, { headers: { Authorization: `Bearer ${token}` } });
        setUploads((prev) => prev.filter((u) => u.upload_id !== upload.upload_id));
        toast.success("Upload deleted");
        if (onUploadComplete) onUploadComplete();
      } catch {
        toast.error("Failed to delete upload");
      }
    });
  };

  const handleRunSchedulerNow = async () => {
    setTriggeringScheduler(true);
    try {
      await axios.post(`${API}/reporting/marketplace/scheduler/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Daily fetch triggered — see Pending Jobs");
      setTimeout(fetchUploads, 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggeringScheduler(false);
    }
  };

  const handleTestConnection = async (ssp) => {  setTestingSSP(ssp);
    try {
      const sspKey = sspToApiKey(ssp);
      const res = await axios.post(
        `${API}/reporting/marketplace/api-test/${sspKey}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.ok) {
        toast.success(`${res.data.ssp}: connection healthy (${res.data.latency_ms}ms)`, {
          description: res.data.message,
        });
      } else {
        toast.error(`${res.data.ssp}: ${res.data.message}`);
      }
      fetchUploads(); // Refresh health badges
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingSSP(null);
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return null;
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return dt.toLocaleDateString();
  };

  const getHealthDisplay = (health) => {
    const map = {
      healthy:  { color: "#22c55e", label: "Healthy",  Icon: CheckCircle2 },
      stale:    { color: "#f59e0b", label: "Stale",    Icon: AlertCircle },
      fetching: { color: "#3b82f6", label: "Fetching", Icon: Activity,  pulse: true },
      error:    { color: "#ef4444", label: "Error",    Icon: AlertCircle },
    };
    return map[health] || { color: "#6b7280", label: "Unknown", Icon: AlertCircle };
  };

  const openFetchDialog = (ssp) => {
    setActiveFetchSSP(ssp);
    setFetchResult(null);
    // Default to current month
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    setFetchDateFrom(`${y}-${m}-01`);
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    setFetchDateTo(`${y}-${m}-${String(lastDay).padStart(2, "0")}`);
    setShowFetchDialog(true);
  };

  const sspToApiKey = (ssp) => {
    // Normalize SSP display name → backend route key
    const map = {
      "index exchange": "index",
      "indexexchange": "index",
      "ix": "index",
      "microsoft": "microsoft",
      "microsoft xandr": "microsoft",
      "xandr": "microsoft",
      "pubmatic": "pubmatic",
      "equativ": "equativ",
      "nexxen": "nexxen",
      "magnite": "magnite",
      "rubicon": "magnite",
      "google": "google",
      "media.net": "medianet",
      "medianet": "medianet",
      "media net": "medianet",
    };
    const lower = (ssp || "").toLowerCase().trim();
    return map[lower] || lower.replace(/\s+/g, "");
  };

  const handleApiFetch = async () => {
    if (!fetchDateFrom || !fetchDateTo) { toast.error("Select date range"); return; }
    setFetchingSSP(activeFetchSSP);
    setFetchResult(null);
    try {
      const res = await axios.post(
        `${API}/reporting/marketplace/api-fetch/${sspToApiKey(activeFetchSSP)}`,
        { from_date: fetchDateFrom, to_date: fetchDateTo },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setFetchResult(res.data);
      if (res.data.row_count > 0) {
        toast.success(`Fetched ${res.data.row_count} rows from ${res.data.ssp_source} API`);
        fetchUploads();
        if (onUploadComplete) onUploadComplete();
      } else if (res.data.delivery === "email") {
        toast.success("Report triggered — check your email within the hour");
      } else if (res.data.status === "pending") {
        toast.info(res.data.message || `${res.data.ssp_source} report queued — refresh uploads list in a few minutes`);
        fetchUploads();
      } else {
        toast.info(res.data.message || "No data returned for this date range");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "API fetch failed");
    } finally {
      setFetchingSSP(null);
    }
  };

  const closeUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadFile(null);
    setUploadResult(null);
  };

  const sspColors = {
    PubMatic: "#22c55e", Nexxen: "#3b82f6", "Index Exchange": "#f59e0b",
    Equativ: "#8b5cf6", Magnite: "#ec4899", Microsoft: "#06b6d4", OpenX: "#f97316",
    Google: "#ea4335", "Media.net": "#00a0df",
  };

  const scheduleFor = (ssp) => schedules.find((s) => s.ssp_source === ssp);

  const handleScheduleSaved = (saved) => {
    setSchedules((prev) => prev.map((s) => (s.key === saved.key ? { ...s, ...saved } : s)));
    axios
      .get(`${API}/reporting/marketplace/scheduler`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setScheduler(res.data); setSchedules(res.data.schedules || []); })
      .catch(() => {});
  };

  return (
    <div className="space-y-6">
      {/* Scheduler Banner */}
      {scheduler && (
        <Card className="bg-gradient-to-r from-[#3b82f6]/8 to-transparent border-[#3b82f6]/20" data-testid="scheduler-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-9 h-9 rounded-lg bg-[#3b82f6]/15 flex items-center justify-center shrink-0">
                <Calendar className="w-4 h-4 text-[#3b82f6]" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-white text-xs font-mono font-medium">Automated Fetch Schedule</p>
                <p className="text-[#ebebeb]/50 text-[11px] font-mono mt-0.5">
                  fetches yesterday&apos;s data · set each SSP&apos;s time (UTC) on its card below
                </p>
              </div>
              <div className="text-right">
                <p className="text-[#ebebeb]/40 text-[10px] font-mono">Next run</p>
                <p className="text-[#3b82f6] text-xs font-mono" data-testid="scheduler-next-run">
                  {scheduler.next_run
                    ? new Date(scheduler.next_run).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                    : "—"}
                </p>
              </div>
              <button
                onClick={handleRunSchedulerNow}
                disabled={triggeringScheduler}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-mono border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors disabled:opacity-50"
                data-testid="scheduler-run-now-btn"
              >
                {triggeringScheduler ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Run all now
              </button>
            </div>
            {Array.isArray(scheduler.jobs) && scheduler.jobs.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {scheduler.jobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`scheduler-job-${j.id}`}>
                    <div className="min-w-0">
                      <p className="text-[#ebebeb]/80 text-[11px] font-mono truncate">{j.label}</p>
                      <p className="text-[#ebebeb]/40 text-[10px] font-mono truncate">
                        {j.schedule} · {j.ssps?.join(", ") || "—"}
                      </p>
                    </div>
                    <p className="text-[#3b82f6] text-[10px] font-mono whitespace-nowrap">
                      {j.next_run
                        ? new Date(j.next_run).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* API Connections Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white font-mono text-sm font-medium" data-testid="api-connections-title">SSP Connections</p>
            <p className="text-[#ebebeb]/40 text-xs font-mono mt-0.5">Connect to SSP APIs for automated data fetching, or upload CSV files manually</p>
          </div>
          <button onClick={() => setShowUploadDialog(true)} className="ingenium-button" data-testid="marketplace-upload-btn">
            <span className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload CSV</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {connections.map((conn) => {
            const color = sspColors[conn.ssp_source] || "#ff3737";
            const health = conn.has_api ? getHealthDisplay(conn.health) : null;
            const sspKey = conn.ssp_source.toLowerCase().replace(/\s/g, "-");
            return (
              <Card
                key={conn.ssp_source}
                className="bg-[#1e2124] border-[#2a2d31] hover:border-[#3a3d41] transition-colors"
                data-testid={`ssp-connection-${sspKey}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-medium"
                      style={{ backgroundColor: `${color}15`, color }}
                    >
                      {conn.ssp_source}
                    </span>
                    {conn.has_api ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-mono"
                        style={{ color: health.color }}
                        title={conn.last_fetch_error || health.label}
                        data-testid={`health-${sspKey}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${health.pulse ? "animate-pulse" : ""}`}
                          style={{ backgroundColor: health.color, boxShadow: `0 0 6px ${health.color}` }}
                        />
                        {health.label}
                        {conn.pending_count > 0 && (
                          <span className="ml-1 px-1 rounded bg-[#3b82f6]/20 text-[#3b82f6]">
                            {conn.pending_count} pending
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[#ebebeb]/30 text-[10px] font-mono">
                        <FileSpreadsheet className="w-3 h-3" /> CSV only
                      </span>
                    )}
                  </div>

                  <p className="text-[#ebebeb]/50 text-[10px] font-mono mb-3">{conn.display}</p>

                  {conn.has_api && (
                    <div className="space-y-2">
                      <div className="text-[10px] font-mono text-[#ebebeb]/40 flex items-center justify-between">
                        <span>Last sync:</span>
                        <span className="text-[#ebebeb]/60">
                          {conn.last_fetch
                            ? `${formatRelativeTime(conn.last_fetch)}${conn.last_fetch_rows ? ` · ${conn.last_fetch_rows.toLocaleString()} rows` : ""}`
                            : "Never"}
                        </span>
                      </div>

                      <div className="grid grid-cols-5 gap-1.5">
                        <button
                          onClick={() => handleTestConnection(conn.ssp_source)}
                          disabled={testingSSP === conn.ssp_source || !conn.connected}
                          className="col-span-2 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[10px] font-mono border border-white/10 text-[#ebebeb]/60 hover:bg-white/5 hover:text-[#ebebeb] transition-colors disabled:opacity-40"
                          data-testid={`test-${sspKey}`}
                          title="Test connection"
                        >
                          {testingSSP === conn.ssp_source ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><Activity className="w-3 h-3" /> Test</>
                          )}
                        </button>
                        <button
                          onClick={() => openFetchDialog(conn.ssp_source)}
                          disabled={!!fetchingSSP || !conn.connected}
                          className="col-span-3 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[10px] font-mono border border-white/10 text-[#ebebeb]/70 hover:bg-white/5 transition-colors disabled:opacity-40"
                          data-testid={`fetch-${sspKey}`}
                        >
                          {fetchingSSP === conn.ssp_source ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Fetching...</>
                          ) : conn.display?.includes("email") ? (
                            <><ArrowDownToLine className="w-3 h-3" /> Trigger Report</>
                          ) : (
                            <><ArrowDownToLine className="w-3 h-3" /> Fetch Data</>
                          )}
                        </button>
                      </div>

                      {scheduleFor(conn.ssp_source) && (
                        <SspScheduleEditor
                          token={token}
                          schedule={scheduleFor(conn.ssp_source)}
                          onSaved={handleScheduleSaved}
                        />
                      )}
                    </div>
                  )}

                  {!conn.has_api && (
                    <button
                      onClick={() => setShowUploadDialog(true)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-mono border border-white/10 text-[#ebebeb]/40 hover:bg-white/5 transition-colors"
                    >
                      <Upload className="w-3 h-3" /> Upload File
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Upload History */}
      <Card className="bg-[#1e2124] border-[#2a2d31]">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-[#2a2d31] flex items-center justify-between">
            <p className="text-[#ebebeb]/60 text-xs font-mono uppercase tracking-wider">Upload History</p>
            <span className="text-[#ebebeb]/30 text-xs font-mono">{uploads.length} uploads</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" /></div>
          ) : uploads.length === 0 ? (
            <div className="text-center p-12">
              <FileSpreadsheet className="w-10 h-10 text-[#ebebeb]/20 mx-auto mb-3" />
              <p className="text-[#ebebeb]/50 font-mono text-sm">No data uploaded yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#2a2d31] hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">SSP</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Source</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Rows</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Revenue</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Spend</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Date Range</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Uploaded</TableHead>
                  <TableHead className="text-[#ebebeb]/50 font-mono text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads.map((u) => (
                  <TableRow key={u.upload_id} className="border-[#2a2d31] hover:bg-white/5" data-testid={`upload-row-${u.upload_id}`}>
                    <TableCell>
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-mono font-medium"
                        style={{ backgroundColor: `${sspColors[u.ssp_source] || "#ff3737"}15`, color: sspColors[u.ssp_source] || "#ff3737" }}
                      >
                        {u.ssp_source}
                      </span>
                      {u.status === "pending" && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-[#f59e0b]/15 text-[#f59e0b]" data-testid={`upload-status-pending-${u.upload_id}`}>
                          <Loader2 className="w-2.5 h-2.5 animate-spin" /> pending
                        </span>
                      )}
                      {u.status === "failed" && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-red-500/15 text-red-400" title={u.error || ""} data-testid={`upload-status-failed-${u.upload_id}`}>
                          failed
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#ebebeb]/60">
                      {u.source === "api" ? (
                        <span className="flex items-center gap-1 text-[#22c55e]"><Wifi className="w-3 h-3" /> API</span>
                      ) : (
                        <span className="flex items-center gap-1 text-[#ebebeb]/40"><FileSpreadsheet className="w-3 h-3" /> {u.filename?.length > 25 ? u.filename.slice(0, 25) + "..." : u.filename}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#ebebeb]">{u.row_count?.toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-xs text-[#22c55e]">${u.summary?.total_revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell className="font-mono text-xs text-[#3b82f6]">${u.summary?.total_spend?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell className="font-mono text-xs text-[#ebebeb]/60">
                      {u.summary?.date_range?.start && u.summary?.date_range?.end
                        ? `${u.summary.date_range.start} → ${u.summary.date_range.end}` : "-"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#ebebeb]/60">
                      {u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => handleDelete(u)} className="p-1.5 rounded hover:bg-red-500/20 text-[#ebebeb]/60 hover:text-red-400" title="Delete upload">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manual Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => !open && closeUploadDialog()}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono">Upload SSP Report</DialogTitle>
            <DialogDescription className="text-[#ebebeb]/50">Upload a CSV or Excel file. The SSP source is auto-detected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div
              className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-[#ff3737]/30 transition-colors cursor-pointer"
              onClick={() => document.getElementById("ssp-file-input").click()}
              data-testid="upload-dropzone"
            >
              <Upload className="w-8 h-8 text-[#ebebeb]/30 mx-auto mb-3" />
              <p className="text-[#ebebeb]/60 font-mono text-sm">{uploadFile ? uploadFile.name : "Click to select a file"}</p>
              <p className="text-[#ebebeb]/30 font-mono text-xs mt-1">.csv, .xlsx, .xls</p>
              <input id="ssp-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden"
                onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadResult(null); }} data-testid="ssp-file-input" />
            </div>
            {uploadResult && (
              <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-lg p-3" data-testid="upload-result">
                <p className="text-[#22c55e] font-mono text-sm font-medium">Upload Successful</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs font-mono text-[#ebebeb]/70">
                  <div className="flex items-center gap-1.5"><Database className="w-3 h-3" /> {uploadResult.ssp_source}</div>
                  <div className="flex items-center gap-1.5"><FileSpreadsheet className="w-3 h-3" /> {uploadResult.row_count} rows</div>
                  <div>Revenue: ${uploadResult.summary?.total_revenue?.toFixed(2)}</div>
                  <div>Spend: ${uploadResult.summary?.total_spend?.toFixed(2)}</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <button onClick={closeUploadDialog} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">{uploadResult ? "Done" : "Cancel"}</button>
            {!uploadResult && (
              <button onClick={handleUpload} disabled={uploading || !uploadFile} className="ingenium-button" data-testid="ssp-upload-submit">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Processing...</> : "Upload & Normalize"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Fetch Dialog */}
      <Dialog open={showFetchDialog} onOpenChange={(open) => { if (!open) { setShowFetchDialog(false); setFetchResult(null); } }}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Wifi className="w-4 h-4 text-[#22c55e]" /> Fetch from {activeFetchSSP} API
            </DialogTitle>
            <DialogDescription className="text-[#ebebeb]/50">Pull data directly from the {activeFetchSSP} API for a date range.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#ebebeb]/50 text-xs font-mono block mb-1">From Date</label>
                <Input type="date" value={fetchDateFrom} onChange={(e) => setFetchDateFrom(e.target.value)} className="form-input text-sm" data-testid="api-fetch-from" />
              </div>
              <div>
                <label className="text-[#ebebeb]/50 text-xs font-mono block mb-1">To Date</label>
                <Input type="date" value={fetchDateTo} onChange={(e) => setFetchDateTo(e.target.value)} className="form-input text-sm" data-testid="api-fetch-to" />
              </div>
            </div>

            {fetchResult && (
              <div className={`${fetchResult.row_count > 0 ? "bg-[#22c55e]/10 border-[#22c55e]/20" : fetchResult.delivery === "email" ? "bg-[#3b82f6]/10 border-[#3b82f6]/20" : "bg-[#f59e0b]/10 border-[#f59e0b]/20"} border rounded-lg p-3`} data-testid="api-fetch-result">
                <p className={`font-mono text-sm font-medium ${fetchResult.row_count > 0 ? "text-[#22c55e]" : fetchResult.delivery === "email" ? "text-[#3b82f6]" : "text-[#f59e0b]"}`}>
                  {fetchResult.row_count > 0 ? `Fetched ${fetchResult.row_count} rows` : fetchResult.delivery === "email" ? "Report Triggered" : "No data found"}
                </p>
                {fetchResult.row_count > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs font-mono text-[#ebebeb]/70">
                    <div>Spend: ${fetchResult.summary?.total_spend?.toFixed(2)}</div>
                    <div>Revenue: ${fetchResult.summary?.total_revenue?.toFixed(2)}</div>
                  </div>
                )}
                {fetchResult.delivery === "email" && (
                  <p className="text-[#ebebeb]/50 text-xs font-mono mt-2">Report will be delivered to your email. Upload the CSV once received.</p>
                )}
                {fetchResult.message && !fetchResult.delivery && <p className="text-[#ebebeb]/50 text-xs font-mono mt-1">{fetchResult.message}</p>}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => { setShowFetchDialog(false); setFetchResult(null); }} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">
              {fetchResult ? "Done" : "Cancel"}
            </button>
            {!fetchResult && (
              <button onClick={handleApiFetch} disabled={!!fetchingSSP || !fetchDateFrom || !fetchDateTo} className="ingenium-button" data-testid="api-fetch-submit">
                {fetchingSSP ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Fetching...</> : <><ArrowDownToLine className="w-4 h-4 inline mr-2" />Fetch Data</>}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplaceUploads;
